import { z } from "zod";
import { skuFields } from "./admin.js";
import { fulfilledQuantitySchema, idSchema, moneySchema, quantitySchema } from "../primitives.js";

export const createOrderInput = z
  .object({
    supplierId: idSchema,
    lines: z.array(z.object({ skuId: idSchema, qty: quantitySchema })).min(1).max(50),
  })
  .refine((v) => new Set(v.lines.map((l) => l.skuId)).size === v.lines.length, {
    message: "같은 품목을 두 번 담을 수 없습니다",
    path: ["lines"],
  });

/** skus_unit_price_ck requires unit_price > 0 — moneySchema alone allows "0", which is valid for round/deal prices elsewhere but not for a catalog entry's own price. */
const positiveUnitPrice = moneySchema.refine((v) => Number(v) > 0, { message: "가격은 0보다 커야 합니다" });

/** Unlike the admin sku form, a supplier's own catalog entry always needs a price — there's no round/deal to carry one instead. */
export const createSupplierSkuInput = z
  .object({ ...skuFields, unitPrice: positiveUnitPrice })
  .refine((v) => v.isDivisible || Number.isInteger(Number(v.qtyStep)), {
    message: "나눌 수 없는 단위의 주문 단위는 정수여야 합니다",
    path: ["qtyStep"],
  });

export const updateSupplierSkuInput = z
  .object({
    nameKo: z.string().min(1).max(120).optional(),
    nameEn: z.string().max(120).nullish(),
    unit: z.string().min(1).max(20).optional(),
    packSize: z.string().max(40).nullish(),
    isDivisible: z.boolean().optional(),
    qtyStep: quantitySchema.optional(),
    unitPrice: positiveUnitPrice.optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "수정할 값을 하나 이상 입력해야 합니다" });

export const rejectOrderInput = z.object({ reason: z.string().max(500).nullish() });

export const finalizeOrderInput = z.object({
  lines: z.array(z.object({ orderLineId: idSchema, fulfilledQty: fulfilledQuantitySchema })).min(1).max(50),
});

export type CreateOrderInput = z.infer<typeof createOrderInput>;
export type CreateSupplierSkuInput = z.infer<typeof createSupplierSkuInput>;
export type UpdateSupplierSkuInput = z.infer<typeof updateSupplierSkuInput>;
export type RejectOrderInput = z.infer<typeof rejectOrderInput>;
export type FinalizeOrderInput = z.infer<typeof finalizeOrderInput>;
